/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Logger } from '@app/core';
import { Observable, of, Subject } from 'rxjs';
import { ApiQuery } from '@app/core/http/api-query';
import { FormGroup } from '@angular/forms';
import { Client } from '@app/clients/clients/client.model';
import { ClientsService } from '@app/clients/clients/clients.service';
import { ClientsFormService } from '@app/clients/clients/clients-form.service';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';

const log = new Logger('ClientSelectComponent');

@Component({
  selector: 'app-client-select',
  templateUrl: './client-select.component.html',
  styleUrls: ['./client-select.component.scss'],
})
export class ClientSelectComponent implements OnInit, OnDestroy {
  data: Observable<Client[]> = of([]);
  apiQuery: ApiQuery = new ApiQuery();
  loader: boolean;
  @Input() parentFormGroup: FormGroup;
  input$ = new Subject<string>();
  @Input() hideLabel = false;
  @Input() onlyActive = true;
  @Output() select = new EventEmitter();
  @Input() passFormControlName = 'client_id';
  clientFormControlName: any = this.passFormControlName.replace('_id', '');
  lastResponse: any = null;
  @Input() includes: string = null;
  @Input() isInModal = true;


  constructor(private clientsService: ClientsService, private clientsFormService: ClientsFormService) {}

  ngOnDestroy() {
    log.debug('destroy');
  }

  ngOnInit() {
    this.clientFormControlName = this.passFormControlName.replace('_id', '');

    const preSelectedClient = this.parentFormGroup.get(this.clientFormControlName);
    const preSelectedClientId = this.parentFormGroup.get(this.passFormControlName);

    /**
     * Попълва избрания client на едит.
     * Ако има FormControl 'client' в parentFormGroup.
     * Ако няма ще се опита да го get-не по ID
     */
    if (preSelectedClient) {
      /*
       * Следи за промени в client FormControl-a и когато намери валиден Client спира да следи
       */
      const preselectedClientSubscriber = preSelectedClient.valueChanges.subscribe((client: any) => {
        if (client && client.name) {
          this.data = of([client]);
          preselectedClientSubscriber.unsubscribe();
        }
      });
    } else if (preSelectedClientId) {
      /*
       * Ако не е намерен client FormControl-a търси client_id FormControl и се опитва да get-не client-a по ID
       */
      const preselectedClientIdSubscriber = preSelectedClientId.valueChanges.subscribe((clientId: any) => {
        if (clientId) {
          this.loadClientById(clientId);
          preselectedClientIdSubscriber.unsubscribe();
        }
      });
    }
    this.clientSearch();
  }

  loadClientById(id: number) {
    this.loader = true;

    const query = new ApiQuery().addIncludes('country');

    if (this.includes) {
      query.addIncludes(this.includes);
    }

    this.clientsService.show(id, query).subscribe((client: Client) => {
      this.data = of([client]);
      this.loader = false;
    });
  }

  clientSearch() {
    if (this.onlyActive) {
      this.apiQuery.addFilter('active', true);
    }

    this.apiQuery.addInclude('country');

    if (this.includes) {
      this.apiQuery.addIncludes(this.includes);
    }

    this.input$
      .pipe(
        tap(() => (this.loader = true)),
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((term: any) => {
          // За да ме прави излишен request след като се избере нещо
          if (!term) {
            return of(this.lastResponse);
          }

          return this.clientsService
            .index(
              this.apiQuery.addFilters({
                name: term,
              })
            )
            .pipe(
              catchError(() => of([])), // empty list on error
              tap(() => (this.loader = false))
            );
        })
      )
      .subscribe((clients: PaginatedResponse) => {
        this.data = of(clients.data);
        this.lastResponse = clients;
        this.loader = false;
      });
  }

  onChange(client: any) {
    this.select.emit(client);
    if (this.parentFormGroup.get(this.clientFormControlName)) {
      this.parentFormGroup.get(this.clientFormControlName).patchValue(client);
    }
  }

  add(searchTerm: string) {
    this.clientsFormService.open(new Client().deserialize({ name: searchTerm })).then(
      (response) => {
        this.loadClientById(response.id);
        this.parentFormGroup.get(this.passFormControlName).setValue(response.id);
      },
      (err) => {
        log.error(err);
      }
    );
  }

  edit() {
    if (this.parentFormGroup.get(this.passFormControlName).value) {
      this.clientsFormService
        .open(new Client().deserialize({ id: this.parentFormGroup.get(this.passFormControlName).value }))
        .then(
          (response) => {
            this.loadClientById(response.id);
            this.parentFormGroup.get(this.passFormControlName).setValue(response.id);
            if (this.parentFormGroup.get(this.clientFormControlName)) {
              this.parentFormGroup.get(this.clientFormControlName).patchValue(response);
            }
          },
          (err) => {
            log.error(err);
          }
        );
    }
  }
}
